import React, { useLayoutEffect, useRef } from "react";
import { arrayMoveImmutable } from "array-move";
import { InputLabel } from "../../../../../shared/InputLabel/InputLabel";
import { useAppTranslation } from "../../../../../i18n/useAppTranslation";
import { SortableContainer } from "../../shared/SortableContainer/SortableContainer";
import { AddTileButton } from "../../shared/AddTileButton/AddTileButton";
import classes from "./QuestionChoices.module.scss";
import { DND_PRESS_DELAY_MS } from "../../config";
import { ChoiceText } from "./boxes/ChoiceText/ChoiceText";
import { ChoiceImage } from "./boxes/ChoiceImage/ChoiceImage";

export function QuestionChoices({ onAdd, onSortEnd, children, choices }) {
  const { tCommon } = useAppTranslation.Common();
  const { tSettings } = useAppTranslation.Settings();
  const rootRef = useRef(null);
  const pendingFocusCount = useRef(null);

  useLayoutEffect(() => {
    if (
      pendingFocusCount.current !== null &&
      choices.length > pendingFocusCount.current
    ) {
      const inputs = rootRef.current.querySelectorAll('input[type="text"]');
      const input = inputs[inputs.length - 1];
      if (input) {
        input.focus();
      }
    }
    pendingFocusCount.current = null;
  }, [choices]);

  const handleAdd = () => {
    pendingFocusCount.current = choices.length;
    onAdd();
  };

  const handleKeyDown = (event) => {
    if (
      event.defaultPrevented ||
      event.key !== "Enter" ||
      !event.shiftKey ||
      event.ctrlKey ||
      event.metaKey ||
      event.altKey ||
      event.nativeEvent.isComposing ||
      event.keyCode === 229
    ) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    if (!event.repeat) {
      handleAdd();
    }
  };

  return (
    // Keyboard events bubble from the controls within this choices group.
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <div
      ref={rootRef}
      className={classes.root}
      role="group"
      aria-label={tCommon("label.questionChoices")}
      onKeyDown={handleKeyDown}
    >
      <InputLabel>{tCommon("label.questionChoices")}</InputLabel>
      <SortableContainer
        pressDelay={DND_PRESS_DELAY_MS}
        onSortEnd={({ oldIndex, newIndex }) => {
          onSortEnd(arrayMoveImmutable(choices, oldIndex, newIndex));
        }}
      >
        {choices.map(children)}
      </SortableContainer>
      <AddTileButton size="small" onClick={handleAdd}>
        {tSettings("questionnaires.questionBuilder.addQuestionChoice")}
      </AddTileButton>
      <small className={classes.shortcutHint}>
        {tSettings("questionnaires.questionBuilder.addQuestionChoiceShortcut")}
      </small>
    </div>
  );
}

QuestionChoices.Text = ChoiceText;
QuestionChoices.Image = ChoiceImage;
